import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Paper,
  Grid,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import { Send as SendIcon, Notifications as NotificationsIcon } from '@mui/icons-material';
import { apiService } from '../../services/api';
import toast from 'react-hot-toast';

type NotificationTarget = 'topic' | 'device' | 'multiple' | 'all';

interface NotificationFormData {
  target: NotificationTarget;
  topic: string;
  fcmToken: string;
  fcmTokens: string[];
  title: string;
  body: string;
  imageUrl: string;
  data: string; // JSON string
}

const Notifications: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<NotificationFormData>({
    target: 'topic',
    topic: 'android',
    fcmToken: '',
    fcmTokens: [],
    title: '',
    body: '',
    imageUrl: '',
    data: '',
  });

  const handleTargetChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      target: event.target.value as NotificationTarget,
    });
  };

  const handleInputChange = (field: keyof NotificationFormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
  };

  const handleTopicChange = (event: any) => {
    setFormData({
      ...formData,
      topic: event.target.value,
    });
  };

  const handleFcmTokensChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const fcmTokens = value.split(',').map(token => token.trim()).filter(token => token.length > 0);
    setFormData({
      ...formData,
      fcmTokens,
    });
  };

  const validateForm = (): boolean => {
    if (!formData.title.trim()) {
      toast.error('Title is required');
      return false;
    }
    if (!formData.body.trim()) {
      toast.error('Body is required');
      return false;
    }

    if (formData.target === 'topic' && !formData.topic) {
      toast.error('Topic is required');
      return false;
    }

    if (formData.target === 'device' && !formData.fcmToken.trim()) {
      toast.error('FCM token is required');
      return false;
    }

    if (formData.target === 'multiple' && formData.fcmTokens.length === 0) {
      toast.error('At least one FCM token is required');
      return false;
    }

    if (formData.data && formData.data.trim()) {
      try {
        JSON.parse(formData.data);
      } catch (e) {
        toast.error('Data must be valid JSON');
        return false;
      }
    }

    return true;
  };

  const handleSend = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const notification = {
        title: formData.title.trim(),
        body: formData.body.trim(),
        imageUrl: formData.imageUrl.trim() || undefined,
      };

      const data = formData.data.trim() ? JSON.parse(formData.data) : undefined;

      let response;

      switch (formData.target) {
        case 'topic':
          response = await apiService.sendNotificationToTopic(formData.topic, notification, data);
          break;
        case 'device':
          // Use FCM token directly
          response = await apiService.sendNotificationToFcmTokens([formData.fcmToken.trim()], notification, data);
          break;
        case 'multiple':
          response = await apiService.sendNotificationToFcmTokens(formData.fcmTokens, notification, data);
          break;
        case 'all':
          response = await apiService.sendNotificationToAllDevices(notification, data);
          break;
      }

      if (response.success) {
        toast.success('Notification sent successfully!');
        // Reset form
        setFormData({
          target: 'topic',
          topic: 'android',
          fcmToken: '',
          fcmTokens: [],
          title: '',
          body: '',
          imageUrl: '',
          data: '',
        });
      } else {
        toast.error(response.error || 'Failed to send notification');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || error.message || 'Failed to send notification');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <NotificationsIcon sx={{ fontSize: 32 }} />
        <Typography variant="h4" component="h1">
          Push Notifications
        </Typography>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Send Notification
              </Typography>
              <Divider sx={{ mb: 3 }} />

              <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
                <FormLabel component="legend">Target</FormLabel>
                <RadioGroup
                  row
                  value={formData.target}
                  onChange={handleTargetChange}
                >
                  <FormControlLabel value="topic" control={<Radio />} label="Topic" />
                  <FormControlLabel value="device" control={<Radio />} label="Single Token" />
                  <FormControlLabel value="multiple" control={<Radio />} label="Multiple Tokens" />
                  <FormControlLabel value="all" control={<Radio />} label="All Devices" />
                </RadioGroup>
              </FormControl>

              {formData.target === 'topic' && (
                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel>Topic</InputLabel>
                  <Select
                    value={formData.topic}
                    label="Topic"
                    onChange={handleTopicChange}
                  >
                    <MenuItem value="android">Android (All Android devices)</MenuItem>
                    <MenuItem value="ios">iOS (All iOS devices)</MenuItem>
                  </Select>
                </FormControl>
              )}

              {formData.target === 'device' && (
                <TextField
                  fullWidth
                  label="FCM Token"
                  value={formData.fcmToken}
                  onChange={handleInputChange('fcmToken')}
                  sx={{ mb: 3 }}
                  placeholder="Enter FCM token"
                  multiline
                  rows={2}
                  helperText="Enter the FCM token for the device"
                />
              )}

              {formData.target === 'multiple' && (
                <TextField
                  fullWidth
                  label="FCM Tokens (comma-separated)"
                  value={formData.fcmTokens.join(', ')}
                  onChange={handleFcmTokensChange}
                  sx={{ mb: 3 }}
                  placeholder="token1, token2, token3"
                  multiline
                  rows={4}
                  helperText="Enter FCM tokens separated by commas (one per line or comma-separated)"
                />
              )}

              <TextField
                fullWidth
                label="Title"
                value={formData.title}
                onChange={handleInputChange('title')}
                required
                sx={{ mb: 3 }}
                placeholder="Notification title"
              />

              <TextField
                fullWidth
                label="Body"
                value={formData.body}
                onChange={handleInputChange('body')}
                required
                multiline
                rows={4}
                sx={{ mb: 3 }}
                placeholder="Notification message"
              />

              <TextField
                fullWidth
                label="Image URL (Optional)"
                value={formData.imageUrl}
                onChange={handleInputChange('imageUrl')}
                sx={{ mb: 3 }}
                placeholder="https://example.com/image.jpg"
                helperText="URL of the image to display in the notification"
              />

              <TextField
                fullWidth
                label="Additional Data (Optional JSON)"
                value={formData.data}
                onChange={handleInputChange('data')}
                multiline
                rows={3}
                sx={{ mb: 3 }}
                placeholder='{"key": "value"}'
                helperText="Additional data to send with the notification (must be valid JSON)"
              />

              <Button
                variant="contained"
                color="primary"
                startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
                onClick={handleSend}
                disabled={loading}
                fullWidth
                size="large"
              >
                {loading ? 'Sending...' : 'Send Notification'}
              </Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2" gutterBottom>
                  <strong>Topic Notifications:</strong>
                </Typography>
                <Typography variant="body2">
                  • <strong>android</strong> - Sends to all Android devices
                </Typography>
                <Typography variant="body2">
                  • <strong>ios</strong> - Sends to all iOS devices
                </Typography>
              </Alert>

              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2" gutterBottom>
                  <strong>FCM Token Notifications:</strong>
                </Typography>
                <Typography variant="body2">
                  • Use FCM tokens directly from user's device tokens
                </Typography>
                <Typography variant="body2">
                  • You can find FCM tokens in the App Users page
                </Typography>
                <Typography variant="body2">
                  • Tokens are unique per device/app installation
                </Typography>
              </Alert>

              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  Devices automatically subscribe to platform topics (android/ios) when the app initializes.
                </Typography>
              </Alert>

              <Paper sx={{ p: 2, bgcolor: 'grey.100' }}>
                <Typography variant="subtitle2" gutterBottom>
                  Notification Limits:
                </Typography>
                <Typography variant="body2" component="div">
                  • Title: Max 100 characters
                  <br />
                  • Body: Max 500 characters
                  <br />
                  • Image URL: Must be a valid URL
                </Typography>
              </Paper>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Notifications;

